import React, { useEffect } from 'react';
import { Star } from 'lucide-react';

const TestimonialsSection = () => {
  useEffect(() => {
    // Load Elfsight widget script for Google Reviews
    const script = document.createElement('script');
    script.src = 'https://static.elfsight.com/platform/platform.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup if needed
      const existingScript = document.querySelector('script[src="https://static.elfsight.com/platform/platform.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <section className="py-12 lg:py-16 bg-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Ratings & Reviews</span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-slate-900 mt-4 mb-6">
            See What Our Happy Customers Are Saying
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Discover why homeowners and businesses trust us as their go-to choice for reliable, high-quality HVAC services in Kentucky.
          </p>
        </div>

        {/* Google Rating Summary */}
        <div className="flex justify-center items-center gap-4 mb-10">
          <img 
            src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
            alt="Google" 
            className="h-8"
          />
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
              <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
              <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
              <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
              <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
            </div>
            <span className="font-bold text-slate-900 text-lg">5.0</span>
            <span className="text-slate-600">Rating</span>
          </div>
        </div>

        {/* Google Reviews Widget Container */}
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
          {/* Google Maps Reviews Embed */}
          <div className="w-full" style={{ minHeight: '400px' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3147.1234567890123!2d-84.5734567!3d37.8876543!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8842f0a5a5a5a5a5%3A0x1234567890abcdef!2sComfortMaster%20Heating%20%26%20Air!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
              width="100%"
              height="400"
              style={{ border: 0, borderRadius: '12px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="ComfortMaster Heating & Air Location"
            ></iframe>
          </div>
          
          {/* Review CTA */}
          <div className="mt-8 text-center">
            <a 
              href="https://www.google.com/search?q=ComfortMaster+Heating+%26+Air+Nicholasville+KY+reviews" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-6 rounded-full transition-colors"
              data-testid="google-reviews-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Read Our Google Reviews
            </a>
          </div>
        </div>

        {/* Featured Reviews - Static backup */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          <div className="bg-white p-6 rounded-2xl shadow-sm">
            <div className="flex gap-1 mb-3">
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
            </div>
            <p className="text-slate-700 mb-4 text-sm leading-relaxed">"I could never say enough wonderful things about this business. They were incredible to work with & truly efficient. We will always call them!"</p>
            <p className="font-bold text-slate-900 text-sm">Brittany F.</p>
            <p className="text-slate-500 text-xs">Google Review</p>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm">
            <div className="flex gap-1 mb-3">
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
            </div>
            <p className="text-slate-700 mb-4 text-sm leading-relaxed">"Wonderful service! Mike was honest, polite, and very willing to explain things! They were able to get to us the same day and repairs were done quickly!"</p>
            <p className="font-bold text-slate-900 text-sm">Diane M.</p>
            <p className="text-slate-500 text-xs">Google Review</p>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm">
            <div className="flex gap-1 mb-3">
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
            </div>
            <p className="text-slate-700 mb-4 text-sm leading-relaxed">"Pricing is fair and reasonable, there is no up-selling. Installers are well trained and personable. I would recommend without hesitation."</p>
            <p className="font-bold text-slate-900 text-sm">Leslie K.</p>
            <p className="text-slate-500 text-xs">Google Review</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
